import { lazy, Suspense } from 'react';
import { Alert, Skeleton } from '@/ui/primitives';
import { CepForm } from '@/components/CepForm';
import { NotifyDialog } from '@/components/NotifyDialog';
import { ResultPanel } from '@/components/ResultPanel';
import { StationList } from '@/components/StationList';
import type { StationFinder } from '@/hooks/useStationFinder';

/* maplibre-gl is most of the bytes on this page, so it loads as its own chunk: the form
   paints immediately and the map streams in behind it. Most of this traffic is a low-end
   Android phone on mobile data. */
const StationMap = lazy(() =>
  import('@/components/StationMap').then((module) => ({ default: module.StationMap })),
);

/* The page as sales sends it over WhatsApp: one column, the lead scrolls. */
export const StandaloneLayout = ({
  locale,
  setLocale,
  dict,
  stations,
  state,
  result,
  errorKind,
  submitCep,
  useLocation,
  mapFailed,
  onMapUnsupported,
  nearbyStations,
  notifyIntent,
  submitNotify,
}: StationFinder) => {
  /* The whole network is on screen from the start — seeing the stations is the point of
     the page — and the map reframes on the lead once a CEP resolves. When the browser
     cannot run the map the list replaces it, but only once there is something to list:
     an empty "closest stations" heading is noise. */
  const renderMapView = () => {
    if (!mapFailed) {
      return (
        <Suspense fallback={<Skeleton className="h-72 w-full rounded-xl sm:h-96" />}>
          <StationMap
            dict={dict}
            stations={stations}
            origin={result?.coords}
            nearby={nearbyStations}
            onUnsupported={onMapUnsupported}
          />
        </Suspense>
      );
    }
    if (!result) {
      return null;
    }
    return <StationList dict={dict} locale={locale} stations={nearbyStations} isMapFallback />;
  };

  return (
    <div className="min-h-dvh bg-white text-black">
      <main className="mx-auto flex w-full max-w-[560px] flex-col gap-6 px-5 py-8">
        <header className="flex flex-col gap-2">
          <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-grey-800">
            Vammo
          </span>
          <h1 className="text-[28px] font-extrabold leading-[1.1] tracking-[-0.03em] sm:text-4xl">
            {dict.hero.title}
          </h1>
          <p className="text-base leading-relaxed text-grey-800">{dict.hero.subtitle}</p>
        </header>

        <CepForm
          dict={dict}
          onSubmit={submitCep}
          onUseLocation={useLocation}
          isBusy={state === 'busy'}
        />

        {state === 'error' && errorKind ? (
          <Alert variant={errorKind === 'imprecise' ? 'warning' : 'destructive'}>
            {dict.errors[errorKind]}
          </Alert>
        ) : null}

        {result ? (
          <ResultPanel
            /* Remount per answer: without a changing key the reveal plays once and every
               later lookup appears with no transition at all. */
            key={`${result.cep ?? 'geo'}-${result.nearest.address}`}
            dict={dict}
            locale={locale}
            result={result}
            action={
              notifyIntent === null ? undefined : (
                <NotifyDialog
                  dict={dict}
                  intent={notifyIntent}
                  band={result.band}
                  inputType={result.cep === null ? 'geolocation' : 'cep'}
                  onSubmit={submitNotify}
                />
              )
            }
          />
        ) : null}

        {renderMapView()}

        <footer className="mt-2 flex flex-col gap-3 border-t border-grey-200 pt-4">
          <p className="text-[11px] text-grey-800">{dict.footer.privacy}</p>
          <button
            type="button"
            className="self-start text-[11px] font-semibold text-grey-800 underline decoration-grey-400 underline-offset-2"
            onClick={() => {
              setLocale(locale === 'pt-BR' ? 'en' : 'pt-BR');
            }}
          >
            {locale === 'pt-BR' ? 'English' : 'Português'}
          </button>
        </footer>
      </main>
    </div>
  );
};
